/* eslint-disable react-hooks/exhaustive-deps */
import { FloatingPanels } from "orcs-design-system";
import React, { useEffect, useMemo, useState } from "react";
import { isEmpty } from "lodash";
import { useOrgChartContext } from "src/components/OrgChart/context/OrgChartContext";
import { EventBus } from "src/services/eventEmitter";
import {
  addLocalStorageItem,
  getLocalStorageItem,
} from "src/services/localStorage";
import { ORG_CHART_DEFAULT_PANEL_ID } from "src/components/OrgChart/utils/storeInLocalStorage";
import { SOURCE_TYPES } from "src/components/AIContent/consts";
import { useWorkspaceFeatureFlags } from "src/contexts/global/WorkspaceContext";
import { AISummaryWithAgentCore } from "../../../AIContent/AISummaryWithAgentCore";
import ViewOptions from "./panels/ViewOptions";
import Properties from "./panels/Properties";
import Legend from "./panels/Legend";
import { PANEL_ID, ORG_CHART_PANEL_EVENTS } from "./const";
import { Details } from "./panels/Details/Details";

const BOTTOM_PADDING = "30px";

const LegendPanel = {
  id: PANEL_ID.legend,
  iconName: "palette",
  title: "Legend",
  content: <Legend />,
};

const ViewOptionsPanel = {
  id: PANEL_ID.viewOptions,
  iconName: "cog",
  title: "View Options",
  content: <ViewOptions />,
};

const PropertiesPanel = {
  id: PANEL_ID.properties,
  iconName: "filter",
  title: "Properties",
  content: <Properties />,
};

const DetailsPanel = {
  id: PANEL_ID.details,
  iconName: "info-circle",
  title: "Details",
  content: <Details />,
};

const AISummaryPanel = () => {
  const { state } = useOrgChartContext();
  const [id, setId] = useState();
  const [isPanelVisible, setIsPanelVisible] = useState(false);

  useEffect(() => {
    const newId =
      state?.expandedPanel?.data?.selectedEntity || state.currentViewTeam?.id;

    if (newId) {
      setId(newId);
    }
  }, [state?.expandedPanel?.data?.selectedEntity, state.currentViewTeam?.id]);

  // Listen for panel selection changes
  useEffect(() => {
    const handlePanelChange = (panelId) => {
      setIsPanelVisible(panelId === PANEL_ID.ai);
    };

    EventBus.on(
      ORG_CHART_PANEL_EVENTS.SELECTED_PANEL_CHANGED,
      handlePanelChange
    );

    return () => {
      EventBus.off(
        ORG_CHART_PANEL_EVENTS.SELECTED_PANEL_CHANGED,
        handlePanelChange
      );
    };
  }, []);

  if (id) {
    return (
      <AISummaryWithAgentCore
        sourceId={SOURCE_TYPES.team_summary}
        subjectId={id}
        title=""
        isPanelVisible={isPanelVisible}
      />
    );
  }
  return undefined;
};

const OrgChartPanel = () => {
  const { state } = useOrgChartContext();
  const flags = useWorkspaceFeatureFlags();

  // listen to panel change events so we can retain panel state across page loads
  useEffect(() => {
    const StoreSelectedPanel = (paneId) => {
      // store selected panel in local storage
      addLocalStorageItem(ORG_CHART_DEFAULT_PANEL_ID, paneId);
    };

    // register event listeners
    EventBus.on(
      ORG_CHART_PANEL_EVENTS.SELECTED_PANEL_CHANGED,
      StoreSelectedPanel
    );

    // on unmount, deregister event listeners
    return () => {
      EventBus.off(
        ORG_CHART_PANEL_EVENTS.SELECTED_PANEL_CHANGED,
        StoreSelectedPanel
      );
    };
  }, []);

  // calc panels to display based on state
  const panelsToDisplay = useMemo(() => {
    const panels = [];

    // ai panel
    if (flags?.enableAISummaryOnTeamDetailsPage) {
      const AIPanel = {
        id: PANEL_ID.ai,
        iconName: "sparkles",
        title: "AI Summary",
        content: <AISummaryPanel />,
      };
      panels.push(AIPanel);
    }

    // legend panel (always show)
    panels.push(LegendPanel);

    // view options panel
    panels.push(ViewOptionsPanel);

    // only show properties panel if there are loaded teams or members
    if (!isEmpty(state?.teams) || !isEmpty(state?.members)) {
      panels.push(PropertiesPanel);
    }

    // legend panel (always show)
    panels.push(DetailsPanel);

    return panels;
  }, [state, flags?.enableAISummaryOnTeamDetailsPage]);

  const defaultPanelId = useMemo(() => {
    // get default selected panel from local storage
    const storedPanelId = getLocalStorageItem(ORG_CHART_DEFAULT_PANEL_ID);

    // have default panel? (null = closed)
    if (storedPanelId !== undefined) {
      return storedPanelId;
    }

    // fallback to details panel
    return PANEL_ID.details;
  }, []);

  return (
    <FloatingPanels
      panels={panelsToDisplay}
      defaultSelectedPanelId={defaultPanelId}
      containerHeight={`calc(100% - ${BOTTOM_PADDING})`}
      position={{
        right: 15,
        top: 15,
      }}
      alignPanels="right"
      eventBus={EventBus}
    />
  );
};

export default OrgChartPanel;
